import * as React from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Title3, Button } from '@fluentui/react-components';
import { Add20Regular } from '@fluentui/react-icons';
import IntegrationGrid from './components/IntegrationGrid';
import IntegrationForm from './components/IntegrationForm';
import { useIntegration } from '../../hooks/useIntegration';
import { Integration, IntegrationCreate } from '../../types/integration';
import { ApiErrorInfo } from '../../types/api';
import ConfirmDialog from '../../components/ConfirmDialog';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingHorizontalXL,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalL,
  },
  title: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  content: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow16,
    padding: tokens.spacingHorizontalXXL,
  },
  drawer: {
    // Drawer lateral
  },
});

const IntegrationManagement: React.FC = () => {
  const styles = useStyles();
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [editingIntegration, setEditingIntegration] = React.useState<Integration | undefined>();
  const [wasSaving, setWasSaving] = React.useState(false);
  const [currentError, setCurrentError] = React.useState<ApiErrorInfo | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [integrationToDelete, setIntegrationToDelete] = React.useState<Integration | null>(null);

  const { integrations, listLoading, listError, saveLoading, saveError, userLoading, createIntegration, updateIntegration, deleteIntegration, fetchIntegrations } = useIntegration();

  // Sincronizar erro atual com saveError, mas permitir limpeza
  React.useEffect(() => {
    if (saveError) {
      setCurrentError(saveError);
    }
  }, [saveError]);

  // Carregar integrações ao montar o componente, mas só após o usuário estar carregado
  React.useEffect(() => {
    if (!userLoading) {
      fetchIntegrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoading]);

  const handleAdd = () => {
    setEditingIntegration(undefined);
    setCurrentError(null); // Limpar erro anterior ao abrir novo cadastro
    setIsDrawerOpen(true);
  };

  const handleEdit = (integration: Integration) => {
    setEditingIntegration(integration);
    setCurrentError(null); // Limpar erro anterior ao editar
    setIsDrawerOpen(true);
  };

  const handleDelete = (id: number) => {
    const integration = integrations.find(i => i.id === id);
    if (integration) {
      setIntegrationToDelete(integration);
      setDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (integrationToDelete) {
      try {
        await deleteIntegration(integrationToDelete.id);
        // Após exclusão bem-sucedida, atualizar a lista
        fetchIntegrations();
      } catch (error) {
        // Erro será tratado pelo estado error do hook
        console.error('Erro ao excluir integração:', error);
      }
    }
    setDeleteDialogOpen(false);
    setIntegrationToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setIntegrationToDelete(null);
  };

  const handleSaveIntegration = async (integrationData: IntegrationCreate) => {
    setWasSaving(true);
    setCurrentError(null); // Limpar erro anterior
    try {
      if (editingIntegration) {
        await updateIntegration(editingIntegration.id, integrationData);
      } else {
        await createIntegration(integrationData);
      }
      // Sucesso será tratado pelo useEffect abaixo
    } catch (error) {
      // Erro será tratado pelo estado error do hook useIntegration
      console.error('Erro ao salvar integração:', error);
      // Não definir setWasSaving(false) aqui - deixar para o useEffect
    }
  };

  // Fechar drawer quando a operação de salvar for concluída
  React.useEffect(() => {
    if (wasSaving && !saveLoading) {
      if (!saveError) {
        // Sucesso: fechar drawer e atualizar lista
        setIsDrawerOpen(false);
        fetchIntegrations();
        setWasSaving(false);
        setEditingIntegration(undefined);
        setCurrentError(null);
      } else {
        // Houve erro, manter drawer aberto para mostrar erro
        setWasSaving(false);
        // currentError será atualizado pelo useEffect que sincroniza com saveError
      }
    }
  }, [wasSaving, saveLoading, saveError, fetchIntegrations]);

  const handleCancel = () => {
    setIsDrawerOpen(false);
    setEditingIntegration(undefined);
    setCurrentError(null); // Limpar erro ao cancelar
  };

  // Limpar erro quando o drawer for fechado
  React.useEffect(() => {
    if (!isDrawerOpen) {
      // O erro será limpo automaticamente quando o componente for re-renderizado
    }
  }, [isDrawerOpen]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title3 className={styles.title}>Integrações</Title3>
        <Button
          appearance="primary"
          size="large"
          icon={<Add20Regular />}
          onClick={handleAdd}
          className={styles.addButton}
        >
          Nova Integração
        </Button>
      </div>

      <div className={styles.content}>
        <IntegrationGrid
          integrations={integrations}
          loading={listLoading}
          error={listError?.message || null}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Excluir Integração"
        message={`Tem certeza que deseja excluir a integração "${integrationToDelete?.empresa}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        intent="danger"
      />

      <Drawer
        open={isDrawerOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        position="end"
        size="large"
        className={styles.drawer}
      >
        <DrawerHeader>
          <DrawerHeaderTitle>
            {editingIntegration ? 'Editar Integração' : 'Nova Integração'}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          <IntegrationForm
            integration={editingIntegration}
            onSave={handleSaveIntegration}
            onCancel={handleCancel}
            loading={saveLoading}
            error={currentError}
          />
        </DrawerBody>
      </Drawer>
    </div>
  );
};

export default IntegrationManagement;